<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import BaseHorizontalList from '@/components/base/BaseHorizontalList.vue'
import BaseVideoThumnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { Lecture, LectureSummary } from '@/types/lecture.types'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import LastVideoItem from './LastVideoItem.vue'

const isMobile = useIsMobile()
const router = useRouter()

const selectedLecture = ref<Lecture | undefined>(undefined)
const lectureSummary = ref<LectureSummary>()

onMounted(async () => {
  const response = await lectureService.getLectureSummary()
  lectureSummary.value = response
  selectedLecture.value = response.currentWatchLectures[0]
})

const scrollbarOptions = {
  maxScrollbarLength: 200,
  minScrollbarLength: 200,
  scrollYMarginOffset: 0,
}

const isEmpty = computed(() => {
  return (
    lectureSummary.value?.currentWatchLectures.length === 0 ||
    lectureSummary.value?.watchLectureCount === 0 ||
    lectureSummary.value?.totalWatchSeconds === 0
  )
})

const computedTotalWatchSeconds = computed(() => {
  if (!lectureSummary.value?.totalWatchSeconds) return '00:00:00'
  const hours = Math.floor(lectureSummary.value?.totalWatchSeconds / 3600)
  const minutes = Math.floor((lectureSummary.value?.totalWatchSeconds % 3600) / 60)
  const seconds = lectureSummary.value?.totalWatchSeconds % 60

  if (hours > 0) {
    return `${hours}시간 ${minutes}분`
  }

  if (minutes > 0) {
    return `${minutes}분`
  }

  return `${seconds}초`
})

const handleSelectedLecture = (lecture: Lecture) => {
  selectedLecture.value = lecture
}

const handleVideoClick = (lectureIdx: number) => {
  router.push(`/video/${lectureIdx}`)
}
</script>

<template>
  <div v-if="!isEmpty" class="dashboard-info-section">
    <div class="dashboard-info-section-left">
      <div class="dashboard-info-video">
        <BaseVideoThumnail
          v-if="selectedLecture"
          :lecture="selectedLecture"
          :show-level="true"
          :show-play-button="true"
          :border-radius="isMobile ? 0 : 8"
          @video-click="handleVideoClick"
        />
      </div>
      <div class="dashboard-info-row">
        <div class="dashboard-info-row-item">
          <div class="white" :class="{ 'title-sm': !isMobile, 'body-sm': isMobile }">
            총 수강시간
          </div>
          <div class="white" :class="{ 'title-lg': !isMobile, 'title-sm': isMobile }">
            {{ computedTotalWatchSeconds }}
          </div>
        </div>
        <div class="dashboard-info-row-item">
          <div class="white" :class="{ 'title-sm': !isMobile, 'body-sm': isMobile }">
            시청한 강좌
          </div>
          <div class="white" :class="{ 'title-lg': !isMobile, 'title-sm': isMobile }">
            {{ lectureSummary?.watchLectureCount }}
          </div>
        </div>
      </div>
    </div>

    <PerfectScrollbar
      v-if="!isMobile"
      class="dashboard-info-section-right"
      :options="scrollbarOptions"
    >
      <div v-for="lecture in lectureSummary?.currentWatchLectures" :key="lecture.idx">
        <LastVideoItem
          class="cursor-pointer"
          :lecture="lecture"
          :selected="lecture.idx === selectedLecture?.idx"
          @click="handleSelectedLecture(lecture)"
        />
      </div>
    </PerfectScrollbar>
    <BaseHorizontalList
      v-else
      class="dashboard-info-section-last-video-list"
      title="최근 본 영상"
      :marginTop="32"
      :gap="12"
    >
      <div v-for="lecture in lectureSummary?.currentWatchLectures" :key="lecture.idx">
        <LastVideoItem
          class="cursor-pointer"
          :lecture="lecture"
          :selected="lecture.idx === selectedLecture?.idx"
          @click="handleSelectedLecture(lecture)"
        />
      </div>
    </BaseHorizontalList>
  </div>
</template>

<style scoped>
@import 'vue3-perfect-scrollbar/style.css';

.dashboard-info-section {
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  height: 657px;
  gap: 20px;
  margin-bottom: 60px;
}

.dashboard-info-section-left {
  flex: 847;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 24px;
}

.dashboard-info-section-right {
  flex: 413;
  width: 413px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  overflow-y: overlay;
  overflow-x: hidden;
  background-color: var(--gray-800);
}

.dashboard-info-video {
  width: 100%;
  height: 477px;
  position: relative;
  border-radius: 8px;
}

.dashboard-info-video-program-tag {
  position: absolute;
  top: 12px;
  left: 12px;
}

.dashboard-info-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  gap: 20px;
}

.dashboard-info-row-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 29px;
  background-color: var(--gray-800);
  border-radius: 8px;
  padding: 20px;
  box-sizing: border-box;
}

.dashboard-info-video-info-container {
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
}

.dashboard-info-video-info {
  position: absolute;
  bottom: 22px;
  left: 12px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 6px;
}

.dashboard-info-video-button-container {
  position: absolute;
  align-self: flex-end;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.dashboard-info-video-button-heart {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

@media (max-width: 1365px) {
  .dashboard-info-video {
    height: auto;
  }

  .dashboard-info-section {
    width: 100%;
    display: inline;
    flex-direction: column;
    align-items: start;
    justify-content: start;
    height: 100%;
    margin-bottom: 32px;
  }

  .dashboard-info-row {
    padding: 0px 20px;
    gap: 7px;
  }

  .dashboard-info-row-item {
    gap: 12px;

    padding: 12px;
  }

  .dashboard-info-section-last-video-list {
    margin-bottom: 32px;
  }
}
</style>
